import { useCallback, useMemo, useState } from "react";
import { useApi } from "@/hooks/useApi";
import { apiPost } from "@/lib/api";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";
import type { ScheduleData } from "@/lib/types";
import ScheduleCalendar from "./ScheduleCalendar";
import ScheduleSidebar from "./ScheduleSidebar";

function currentYm(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function shiftYm(ym: string, delta: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function ymLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return `${y}년 ${Number(m)}월`;
}

// 메인 홈과 /calendar 페이지가 «같은 컴포넌트» 를 공유한다. (두 벌로 만들지 않는다)
// 출근도장 + 일정 + 생일을 schedule.php 한 번으로 받아 한 달력에 합쳐 보여준다.
export default function ScheduleSection() {
  const { member, requireLogin } = useAuth();
  const { toast } = useToast();
  const [ym, setYm] = useState(currentYm());
  const [checking, setChecking] = useState(false);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  const { data, loading, error, reload } = useApi<ScheduleData>(`schedule.php?ym=${ym}`);

  const attend = data?.attend;
  const todayCnt = attend?.today_cnt ?? 0;
  const myMonthCnt = attend?.my_month_cnt ?? 0;
  const myToday = attend?.my_today === 1;
  const todayList = useMemo(() => attend?.today_list ?? [], [attend]);
  const rank = useMemo(() => attend?.rank ?? [], [attend]);
  const myDays = useMemo(() => attend?.my_days ?? [], [attend]);
  const upcomingBirth = useMemo(() => data?.upcoming_birth ?? [], [data]);
  const events = useMemo(() => data?.events ?? [], [data]);
  const today = data?.today ?? "";

  // 출근 인원수: attend.by_day 가 기준. (kind:"attend" 이벤트가 따로 오면 빈 칸만 채운다)
  const attendByDay = useMemo(() => {
    const map: Record<string, number> = { ...(attend?.by_day ?? {}) };
    events.forEach((ev) => {
      if (ev.kind === "attend" && ev.date && map[ev.date] == null) {
        map[ev.date] = Number(ev.cnt) || 1;
      }
    });
    return map;
  }, [attend, events]);

  const isCurrentMonth = ym === currentYm();
  const canCheck = !myToday && !checking && isCurrentMonth;

  const changeYm = useCallback((next: string) => {
    setExpandedDay(null);
    setYm(next);
  }, []);

  const handleToggleDay = useCallback((day: string) => {
    setExpandedDay((prev) => (prev === day ? null : day));
  }, []);

  const handleCheck = useCallback(async () => {
    if (myToday) return;
    if (!requireLogin()) return;
    setChecking(true);
    try {
      const res = await apiPost<{ today_cnt: number; my_month_cnt: number }>(
        "attend_check.php",
        {}
      );
      toast(res.msg || "출근도장을 찍었습니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "도장을 찍지 못했습니다.", "error");
    } finally {
      setChecking(false);
    }
  }, [myToday, requireLogin, toast, reload]);

  const checkButtonClass = canCheck
    ? "bg-accent-500 text-primary-950 hover:bg-accent-600"
    : "bg-background-200 text-foreground-500";

  return (
    <div>
      {/* 상단 바: 월 이동 + 이번달 + 출근도장(데스크톱) */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 p-1">
            <button
              type="button"
              onClick={() => changeYm(shiftYm(ym, -1))}
              aria-label="이전 달"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-600 transition-colors hover:bg-background-100"
            >
              <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
            </button>
            <span className="min-w-[110px] text-center font-heading text-sm font-bold text-foreground-950">
              {ymLabel(ym)}
            </span>
            <button
              type="button"
              onClick={() => changeYm(shiftYm(ym, 1))}
              aria-label="다음 달"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-600 transition-colors hover:bg-background-100"
            >
              <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
            </button>
          </div>

          <button
            type="button"
            onClick={() => changeYm(currentYm())}
            disabled={isCurrentMonth}
            className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:text-foreground-400"
          >
            이번달
          </button>
        </div>

        <button
          type="button"
          onClick={handleCheck}
          disabled={!canCheck}
          className={`hidden cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 py-3 text-sm font-bold transition-colors lg:inline-flex disabled:cursor-not-allowed ${checkButtonClass}`}
        >
          <i
            className={myToday ? "ri-checkbox-circle-fill" : "ri-fingerprint-line"}
            aria-hidden="true"
          ></i>
          {myToday ? "오늘 도장 완료 ✓" : checking ? "찍는 중…" : "출근도장 찍기"}
        </button>
      </div>

      {/* 통계 3개 */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
          <div className="font-heading text-2xl font-bold text-primary-700 md:text-3xl">
            {todayCnt}
            <span className="ml-0.5 text-base font-semibold">명</span>
          </div>
          <div className="mt-1 text-sm text-foreground-500">오늘 출근</div>
        </div>
        <div className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
          <div className="font-heading text-2xl font-bold text-secondary-800 md:text-3xl">
            {myMonthCnt}
            <span className="ml-0.5 text-base font-semibold">일</span>
          </div>
          <div className="mt-1 text-sm text-foreground-500">이번 달 내 출근</div>
        </div>
        <div className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
          <div className="font-heading text-2xl font-bold text-accent-600 md:text-3xl">
            {member ? "출근 중" : "미로그인"}
          </div>
          <div className="mt-1 text-sm text-foreground-500">
            {member ? `${member.name}님` : "로그인 후 도장을 찍을 수 있어요"}
          </div>
        </div>
      </div>

      {/* 본문 */}
      {loading ? (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Skeleton className="h-96 w-full rounded-xl lg:col-span-2" />
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      ) : error ? (
        <div className="mt-6">
          <EmptyState
            icon="ri-error-warning-line"
            title="일정을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* 달력 (데스크톱에서는 오른쪽) */}
          <div className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-6 lg:order-2 lg:col-span-2">
            <h3 className="mb-4 font-heading text-base font-bold text-foreground-950">
              {ymLabel(ym)} 일정
            </h3>
            <ScheduleCalendar
              ym={ym}
              events={events}
              attendByDay={attendByDay}
              myDays={myDays}
              today={today}
              expandedDay={expandedDay}
              onToggleDay={handleToggleDay}
            />

            {/* 범례 */}
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3.5 w-3.5 rounded-sm bg-primary-500" aria-hidden="true"></span>
                일정
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3.5 w-3.5 rounded-sm bg-secondary-300" aria-hidden="true"></span>
                생일
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-check-line text-foreground-400" aria-hidden="true"></i>
                출근도장
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="h-3.5 w-3.5 rounded-sm border border-accent-300 bg-accent-50"
                  aria-hidden="true"
                ></span>
                내 출근
              </span>
            </div>
          </div>

          {/* 사이드 (데스크톱에서는 왼쪽, 모바일에서는 달력 아래) */}
          <div className="lg:order-1">
            <ScheduleSidebar
              todayList={todayList}
              rank={rank}
              upcomingBirth={upcomingBirth}
            />
          </div>
        </div>
      )}

      {/* 모바일: 하단 sticky 출근도장 버튼 (탭바 위) */}
      <div
        className="sticky z-[140] mt-6 lg:hidden"
        style={{ bottom: "calc(var(--tabbar-h) + env(safe-area-inset-bottom, 0px))" }}
      >
        <button
          type="button"
          onClick={handleCheck}
          disabled={!canCheck}
          className={`flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-6 py-3.5 text-sm font-bold transition-colors disabled:cursor-not-allowed ${checkButtonClass}`}
        >
          <i
            className={myToday ? "ri-checkbox-circle-fill text-lg" : "ri-fingerprint-line text-lg"}
            aria-hidden="true"
          ></i>
          {myToday ? "오늘 도장 완료 ✓" : checking ? "찍는 중…" : "출근도장 찍기"}
        </button>
      </div>
    </div>
  );
}